import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash } from 'react-icons/fa';
import { commonToast } from '../../../helper/Toast';

export default function TeacherDetail() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [teacher, setTeacher] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Simulate API call to fetch teacher details
        const fetchTeacherDetails = async () => {
            try {
                // Replace with actual API call
                const mockTeacher = {
                    id: parseInt(id),
                    name: "John Smith",
                    email: "john.smith@example.com",
                    phone: "+370 612 34567",
                    classes: [
                        { name: "Class 1A", deckCount: 5, students: 25 },
                        { name: "Class 2B", deckCount: 3, students: 30 }
                    ],
                    status: "active",
                    lastLogin: "2024-03-15",
                    registrationDate: "2024-01-10",
                    totalDecks: 8,
                    totalStudents: 55,
                    averageDeckCompletion: 85,
                    recentActivity: [
                        { date: "2024-03-15", action: "Created new deck", deckName: "Math Basics" },
                        { date: "2024-03-14", action: "Assigned deck", deckName: "Science Quiz" },
                        { date: "2024-03-13", action: "Updated class", className: "Class 1A" }
                    ]
                };
                setTeacher(mockTeacher);
            } catch (error) {
                commonToast(t('error_loading_teacher'), 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchTeacherDetails();
    }, [id, t]);

    // const handleEdit = () => {
    //     navigate(`/admin/teacher/${id}/edit`);
    // };

    const handleDelete = () => {
        if (window.confirm(t('teacher_management.delete_confirm'))) {
            // Add delete logic here
            commonToast(t('teacher_management.delete_success'), 'success');
            navigate('/admin/teacherdetail');
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-screen">{t('loading')}...</div>;
    }

    if (!teacher) {
        return <div className="flex justify-center items-center h-screen">{t('teacher_not_found')}</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin/teacherlist')}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <FaArrowLeft size={20} />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900">{teacher.name}</h1>
                    </div>
                    <div className="flex gap-4">
                        <button
                            // onClick={handleEdit}
                            className="primary-btn"
                        >
                            <FaEdit size={18} />
                            {t('edit')}
                        </button>
                        <button
                            // onClick={handleDelete}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            <FaTrash size={18} />
                            {t('remove')}
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid md:grid-cols-1 grid-cols-2 gap-6">
                    {/* Teacher Information */}
                    <div className="space-y-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h2 className="text-lg font-semibold mb-4">{t('teacher_management.personal_info')}</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('email')}:</span>
                                    <span className="font-medium">{teacher.email}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('teacher_management.phone')}:</span>
                                    <span className="font-medium">{teacher.phone}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('teacher_management.status')}:</span>
                                    <span className={`font-medium ${teacher.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                                        {t(`teacher_management.${teacher.status}`)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('teacher_management.registration_date')}:</span>
                                    <span className="font-medium">{teacher.registrationDate}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('teacher_management.last_login')}:</span>
                                    <span className="font-medium">{teacher.lastLogin}</span>
                                </div>
                            </div>
                        </div>

                        {/* Classes Section */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h2 className="text-lg font-semibold mb-4">{t('teacher_management.classes')}</h2>
                            <div className="space-y-4">
                                {teacher.classes.map((classItem, index) => (
                                    <div key={index} className="bg-white p-3 rounded-lg shadow-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium">{classItem.name}</span>
                                            <span className="text-sm text-gray-500">
                                                {classItem.students} {t('students')}
                                            </span>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-600">
                                            {t('teacher_management.decks_per_class')}: {classItem.deckCount}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Statistics and Activity */}
                    <div className="space-y-6">
                        {/* Statistics */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h2 className="text-lg font-semibold mb-4">{t('statistics')}</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                                    <div className="text-2xl font-bold text-blue-600">{teacher.totalDecks}</div>
                                    <div className="text-sm text-gray-600">{t('teacher_management.total_decks')}</div>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                                    <div className="text-2xl font-bold text-green-600">{teacher.totalStudents}</div>
                                    <div className="text-sm text-gray-600">{t('total_students')}</div>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                                    <div className="text-2xl font-bold text-purple-600">{teacher.averageDeckCompletion}%</div>
                                    <div className="text-sm text-gray-600">{t('average_completion')}</div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h2 className="text-lg font-semibold mb-4">{t('recent_activity')}</h2>
                            <div className="space-y-3">
                                {teacher.recentActivity.map((activity, index) => (
                                    <div key={index} className="bg-white p-3 rounded-lg shadow-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium">{activity.action}</span>
                                            <span className="text-sm text-gray-500">{activity.date}</span>
                                        </div>
                                        <div className="mt-1 text-sm text-gray-600">
                                            {activity.deckName || activity.className}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
